import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { useActions } from 'hooks/useActions';
import { actions as authActions } from 'services/auth';
import { actions as eventActions, EventPayload, eventNames } from 'services/events';
import { selectors as hardwareSelectors, actions as hardwareActions } from 'services/hardware';
import { selectors as kioskSelectors, Kiosk } from 'services/kiosk';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { UNKNOWN } from 'constants/hardware';

const StartupView: React.FC = () => {
  const { logEvent } = useActions(eventActions);
  const { removeMaintenanceToken } = useActions(authActions);
  const { eventDataPing, pingPrinters, scannerPing, paymentPing } = useActions(hardwareActions);
  const navigate = useNavigate();

  const statuses = useSelector(hardwareSelectors.selectAllStatuses);
  const eventData = useSelector(hardwareSelectors.selectEventData);
  const anyHardwareErrors = useSelector(hardwareSelectors.selectAnyHardwareErrors);
  const kioskPrinters = useSelector(kioskSelectors.selectPrinters);
  const paymentHardware = useSelector(kioskSelectors.selectPaymentHardware);
  const scannerHardware = useSelector(kioskSelectors.selectScannerHardware);
  const kiosk = useSelector(kioskSelectors.selectKiosk) as Kiosk;
  const kioskLoading = useSelector(kioskSelectors.selectLoading);

  const hasUnknownStatus = statuses.some((status) => status === UNKNOWN);

  useEffect(() => {
    removeMaintenanceToken();
    localStorage.removeItem('maintenance_token');

    if (!kiosk) return;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Ping the hardware after the kiosk has been fetched, and the printers are registered in the hardware store
    if (!kioskLoading && hasUnknownStatus) {
      if (eventData.status.code === UNKNOWN) eventDataPing();
      if (kioskPrinters.length > 0) {
        pingPrinters({});
      }
      if (paymentHardware) paymentPing();
      if (scannerHardware) scannerPing();
    }
  }, [kioskLoading]);

  useEffect(() => {
    if (!kiosk) return;

    if (!hasUnknownStatus) {
      if (!anyHardwareErrors) {
        const eventPayload: EventPayload = {
          event: eventNames.HARDWARE_CONNECTTION_CONFIRMED,
          category: 'hardware_status'
        };
        logEvent(eventPayload);
      }
      navigate('/ticketing/attract');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnknownStatus]);

  if (!kiosk) return <Navigate to="/" />;

  return (
    <>
      {hasUnknownStatus && <LoadingOverlay message="Please wait while we confirm the hardware status of the kiosk" />}
    </>
  );
};

export default StartupView;
